import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import * as SelectPrimitive from '@radix-ui/react-select'
import * as SwitchPrimitive from '@radix-ui/react-switch'
import {
  Users,
  Search,
  Mail,
  Phone,
  Building2,
  CheckCircle,
  XCircle,
  Shield,
  RefreshCw,
  Key,
  UserCheck,
  UserPlus,
  KeyRound,
  ChevronDown,
  Copy,
  Check,
  Crown,
  AlertCircle,
  Ban,
  Calculator,
  Briefcase,
  Scale,
  User,
  Sparkles,
  ChevronRight,
  FileText,
  ShoppingBag,
  LockKeyhole,
  Settings,
  Trash2,
  Eye,
} from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Label } from '../../components/ui/Label'
import { Textarea } from '../../components/ui/Textarea'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from '../../components/ui/Dialog'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../../components/ui/AlertDialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/DropdownMenu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/Tooltip'
import { DialogHeaderWithIcon } from '../../components/ui/DialogHeaderWithIcon'
import {
  adminApi,
  AdminUser,
  AdminOrg,
  AdminExchange,
  CreateUserBody,
  PlatformRole,
  OrgRole,
  ExchangeRole,
} from '../../services/api/admin'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissionContext } from '../../contexts/PermissionContext'
import { setAccessToken } from '../../services/api/client'
import { roleLabel } from '../../lib/roleFormatting'
import toast from 'react-hot-toast'
import { cn } from '../../lib/utils'
import { PasswordStrengthIndicator } from '../../components/auth/PasswordStrengthIndicator'
import { passwordSchema } from '../../lib/passwordValidation'
import { useConfirm } from '../../contexts/ConfirmProvider'

const impersonateSchema = z.object({
  reason: z.string()
    .min(10, 'Reason must be at least 10 characters')
    .max(500, 'Reason must be 500 characters or fewer'),
})

const setPasswordSchema = z.object({
  newPassword: passwordSchema,
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
})

const EXCHANGE_USER_TYPES = [
  'owner',
  'accounting',
  'real_estate_agent',
  'attorney',
  'member',
  'viewer',
  'custom',
] as const

type ExchangeUserType = typeof EXCHANGE_USER_TYPES[number]

const createUserSchema = z.object({
  email: z.string().email('Must be a valid email address'),
  firstName: z.string().min(1, 'First name is required').max(100, 'First name must be 100 characters or fewer'),
  lastName: z.string().min(1, 'Last name is required').max(100, 'Last name must be 100 characters or fewer'),
  platformRole: z.enum(['platform_admin', 'platform_support']).nullable().optional(),
  orgId: z.string().nullable().optional(),
  orgRole: z.enum(['org_owner', 'org_admin', 'org_staff']).nullable().optional(),
  sendWelcomeEmail: z.boolean().optional(),
  exchangeAccess: z.object({
    exchangeId: z.string().min(1, 'Exchange is required'),
    userType: z.enum(EXCHANGE_USER_TYPES),
    featurePermissions: z.array(z.string()).optional(),
  }).optional(),
}).superRefine((data, context) => {
  const hasOrg = !!data.orgId
  const hasOrgRole = !!data.orgRole
  if (hasOrg && !hasOrgRole) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Org role is required when an org is selected',
      path: ['orgRole'],
    })
  }
  if (hasOrgRole && !hasOrg) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Org is required when an org role is selected',
      path: ['orgId'],
    })
  }
  if (data.exchangeAccess) {
    if (!data.exchangeAccess.userType) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Exchange role is required when assigning exchange access',
        path: ['exchangeAccess', 'userType'],
      })
    }
    if (!data.exchangeAccess.exchangeId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Exchange is required when assigning exchange access',
        path: ['exchangeAccess', 'exchangeId'],
      })
    }
    if (!hasOrg) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Organization is required when assigning exchange access',
        path: ['orgId'],
      })
    }
  }
})

type ImpersonateFormValues = z.infer<typeof impersonateSchema>
type SetPasswordFormValues = z.infer<typeof setPasswordSchema>
type CreateUserFormValues = z.infer<typeof createUserSchema>

const PLATFORM_ROLE_OPTIONS = [
  { value: '__none__', label: 'None' },
  { value: 'platform_support', label: 'Platform Support' },
  { value: 'platform_admin', label: 'Platform Admin' },
]

const EXCHANGE_USER_TYPE_LABELS: Record<ExchangeUserType, string> = {
  owner: 'Owner',
  accounting: 'Accounting',
  real_estate_agent: 'Real Estate Agent',
  attorney: 'Attorney',
  member: 'Member',
  viewer: 'Viewer',
  custom: 'Custom',
}

const EXCHANGE_USER_TYPE_DESCRIPTIONS: Record<ExchangeUserType, string> = {
  owner: 'Full access to all features',
  accounting: 'Access to IRS forms and financial data',
  real_estate_agent: 'Access to upload documents and property info',
  attorney: 'Access to legal documents and compliance',
  member: 'Standard user access',
  viewer: 'Read-only access',
  custom: 'Custom permissions and feature access',
}

const EXCHANGE_USER_TYPE_ICONS: Record<ExchangeUserType, React.FC<{ className?: string }>> = {
  owner: Crown,
  accounting: Calculator,
  real_estate_agent: Briefcase,
  attorney: Scale,
  member: User,
  viewer: Eye,
  custom: Sparkles,
}

const EXCHANGE_FEATURE_LABELS: Record<string, string> = {
  irs_forms: 'IRS Forms',
  documents: 'Documents',
  wizard: 'Exchange Wizard',
  calculator: 'Calculator',
  marketplace: 'Marketplace',
}

const EXCHANGE_FEATURE_ICONS: Record<string, React.FC<{ className?: string }>> = {
  irs_forms: FileText,
  documents: FileText,
  wizard: Building2,
  calculator: Calculator,
  marketplace: ShoppingBag,
}

const PARTICIPANT_ROLES: Array<{
  value: ExchangeRole
  label: string
  description: string
  Icon: React.FC<{ className?: string }>
}> = [
  { value: 'exchanger', label: 'Exchanger', description: 'Property seller executing the exchange', Icon: User },
  { value: 'qi_officer', label: 'QI Officer', description: 'Qualified Intermediary staff member', Icon: Shield },
  { value: 'advisor', label: 'Advisor', description: 'Financial or investment advisor', Icon: Briefcase },
  { value: 'title_company', label: 'Title Company', description: 'Title/escrow company representative', Icon: Building2 },
  { value: 'attorney', label: 'Attorney', description: 'Legal counsel for the transaction', Icon: Scale },
]

const ORG_ROLE_PRESETS: Array<{
  value: OrgRole
  label: string
  description: string
  Icon: React.FC<{ className?: string }>
  color: string
  selectedBg: string
  selectedText: string
}> = [
  {
    value: 'org_owner',
    label: 'Org Owner',
    description: 'Full administrative control. Manages billing, deletes the org, transfers ownership.',
    Icon: Crown,
    color: 'text-amber-500',
    selectedBg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-400',
    selectedText: 'text-amber-700 dark:text-amber-300',
  },
  {
    value: 'org_admin',
    label: 'Org Admin',
    description: 'Manages users, branding, settings, exchanges. Cannot transfer ownership or delete the org.',
    Icon: Shield,
    color: 'text-teal-500',
    selectedBg: 'bg-teal-50 dark:bg-teal-950/40 border-teal-400',
    selectedText: 'text-teal-700 dark:text-teal-300',
  },
  {
    value: 'org_staff',
    label: 'Org Staff',
    description: 'Read-only access to org admin pages. Sees user roster and audit log.',
    Icon: Eye,
    color: 'text-muted-foreground',
    selectedBg: 'bg-muted border-border',
    selectedText: 'text-foreground',
  },
]

function avatarColor(email: string): string {
  let hash = 0
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash)
  }
  const colors = [
    'bg-teal-500',
    'bg-blue-500',
    'bg-violet-500',
    'bg-rose-500',
    'bg-amber-500',
    'bg-emerald-500',
    'bg-cyan-500',
    'bg-indigo-500',
  ]
  return colors[Math.abs(hash) % colors.length]
}

function initials(firstName: string, lastName: string): string {
  return `${(firstName[0] ?? '').toUpperCase()}${(lastName[0] ?? '').toUpperCase()}`
}

const ROLE_BADGE_CLASS: Record<string, string> = {
  org_owner: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
  org_admin: 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300',
  org_staff: 'bg-muted text-muted-foreground',
  platform_admin: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-300',
  platform_support: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
}

function RoleBadge({ role }: { role?: string | null }) {
  if (!role) return null
  const className = ROLE_BADGE_CLASS[role] ?? 'bg-muted text-muted-foreground'
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
        className,
      )}
    >
      {roleLabel(role)}
    </span>
  )
}

function StatusBadge({ verified }: { verified: boolean }) {
  if (verified) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
        <CheckCircle className="h-3 w-3" />
        Active
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300">
      <XCircle className="h-3 w-3" />
      Pending
    </span>
  )
}

function UserCardSkeleton() {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-full bg-muted animate-pulse flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 w-36 bg-muted rounded animate-pulse" />
          <div className="h-3 w-48 bg-muted rounded animate-pulse" />
        </div>
        <div className="h-8 w-8 bg-muted rounded animate-pulse" />
      </div>
    </Card>
  )
}

const SelectField: React.FC<{
  value: string
  onValueChange: (value: string) => void
  placeholder: string
  options: Array<{ value: string; label: string }>
  disabled?: boolean
  'aria-label'?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}> = ({ value, onValueChange, placeholder, options, disabled, ...ariaProps }) => (
  <SelectPrimitive.Root
    value={value || ''}
    onValueChange={onValueChange}
    disabled={disabled}
  >
    <SelectPrimitive.Trigger
      aria-label={ariaProps['aria-label']}
      aria-invalid={ariaProps['aria-invalid']}
      aria-describedby={ariaProps['aria-describedby']}
      className={cn(
        'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm',
        'ring-offset-background placeholder:text-muted-foreground',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        '[&>span]:line-clamp-1',
      )}
    >
      <SelectPrimitive.Value placeholder={placeholder} />
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        className={cn(
          'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
        )}
        position="popper"
      >
        <SelectPrimitive.Viewport className="p-1">
          {options.map((option) => (
            <SelectPrimitive.Item
              key={option.value}
              value={option.value}
              className={cn(
                'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
                'focus:bg-accent focus:text-accent-foreground',
                'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
              )}
            >
              <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
            </SelectPrimitive.Item>
          ))}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  </SelectPrimitive.Root>
)

function toNullable<T>(value: string | null | undefined, noneValue = '__none__'): T | null {
  if (!value || value === noneValue) return null
  return value as unknown as T
}

const UserCard: React.FC<{
  user: AdminUser
  onView: (user: AdminUser) => void
  onEdit: (user: AdminUser) => void
  onPasswordReset: (user: AdminUser) => void
  onImpersonate: (user: AdminUser) => void
  onDemoReset: (user: AdminUser) => void
  onDelete: (user: AdminUser) => void
}> = ({ user, onView, onEdit, onPasswordReset, onImpersonate, onDemoReset, onDelete }) => (
  <Card className="p-5 hover:shadow-md transition-shadow">
    <div className="flex items-center gap-4">
      <div
        className={cn(
          'flex-shrink-0 h-11 w-11 rounded-full flex items-center justify-center text-white text-sm font-semibold',
          avatarColor(user.email),
        )}
        aria-hidden="true"
      >
        {initials(user.firstName, user.lastName)}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-foreground">
            {user.firstName} {user.lastName}
          </span>
          {user.isAdmin && (
            <Shield
              className="h-3.5 w-3.5 text-violet-500"
              aria-label="Platform admin"
            />
          )}
          {user.mfaEnabled && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <LockKeyhole
                      className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400"
                      aria-label="MFA enabled"
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent><p>MFA enabled</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {user.isAdmin
            ? <RoleBadge role="platform_admin" />
            : <StatusBadge verified={user.isIdentityVerified} />
          }
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Mail className="h-3 w-3" />
            {user.email}
          </span>
          {user.phone && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Phone className="h-3 w-3" />
              {user.phone}
            </span>
          )}
          {(user.exchangeCount ?? 0) > 0 && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Building2 className="h-3 w-3" />
              {user.exchangeCount} exchange{(user.exchangeCount ?? 0) !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label={`View details for ${user.firstName} ${user.lastName}`}
                onClick={() => onView(user)}
              >
                <Eye className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent><p>View details</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              aria-label={`Actions for ${user.firstName} ${user.lastName}`}
            >
              <Settings className="h-4 w-4" />
              <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(user)}>
              <Settings className="h-4 w-4 mr-2" />
              Edit user
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPasswordReset(user)}>
              <KeyRound className="h-4 w-4 mr-2" />
              Reset password
            </DropdownMenuItem>
            {!user.isAdmin && (
              <DropdownMenuItem onClick={() => onImpersonate(user)}>
                <UserCheck className="h-4 w-4 mr-2" />
                View as user
              </DropdownMenuItem>
            )}
            {user.email === 'newuser@open1031.ai' && (
              <DropdownMenuItem onClick={() => onDemoReset(user)}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset demo state
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <DropdownMenuItem
                      disabled
                      className="text-amber-600 dark:text-amber-400 opacity-50 cursor-not-allowed"
                    >
                      <Ban className="h-4 w-4 mr-2" />
                      Suspend
                    </DropdownMenuItem>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Coming soon — see issue #112</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {!user.isAdmin && (
              <DropdownMenuItem
                onClick={() => onDelete(user)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  </Card>
)

export const AdminUsersPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const { isPlatformAdmin } = usePermissionContext()
  const confirm = useConfirm()
  const navigate = useNavigate()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [selectedUserDetails, setSelectedUserDetails] = useState<any>(null)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [editForm, setEditForm] = useState<Partial<AdminUser>>({})
  const [editActiveTab, setEditActiveTab] = useState<'profile' | 'permissions' | 'admin'>('profile')
  const [impersonateTarget, setImpersonateTarget] = useState<AdminUser | null>(null)
  const [impersonating, setImpersonating] = useState(false)
  const [addUserOpen, setAddUserOpen] = useState(false)
  const [orgs, setOrgs] = useState<AdminOrg[]>([])
  const [orgsLoading, setOrgsLoading] = useState(false)
  const [orgsError, setOrgsError] = useState(false)
  const [exchanges, setExchanges] = useState<AdminExchange[]>([])
  const [exchangesLoading, setExchangesLoading] = useState(false)
  const [exchangeAccessExpanded, setExchangeAccessExpanded] = useState(false)
  const [creatingUser, setCreatingUser] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [oneTimePassword, setOneTimePassword] = useState<{ email: string; password: string } | null>(null)
  const [oneTimeCopied, setOneTimeCopied] = useState(false)
  const [passwordResetTarget, setPasswordResetTarget] = useState<AdminUser | null>(null)
  const [sendingReset, setSendingReset] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null)
  const [setPasswordTarget, setSetPasswordTarget] = useState<AdminUser | null>(null)
  const [setPasswordResult, setSetPasswordResult] = useState<string | null>(null)
  const [setPasswordResultCopied, setSetPasswordResultCopied] = useState(false)
  const [settingPassword, setSettingPassword] = useState(false)
  const [permissionsSelectedRole, setPermissionsSelectedRole] = useState<ExchangeRole>('exchanger')
  const [permissionsRoles, setPermissionsRoles] = useState<Record<string, ExchangeRole | null>>({})
  const [permissionsInitialRoles, setPermissionsInitialRoles] = useState<Record<string, ExchangeRole | null>>({})
  const [savingPermissions, setSavingPermissions] = useState(false)

  const queryClient = useQueryClient()

  const { data: editingUserDetails } = useQuery({
    queryKey: ['admin', 'user', editingUser?.id ?? ''],
    queryFn: () => adminApi.getUser(editingUser!.id),
    enabled: !!editingUser,
  })

  const editingUserOrgId = useMemo(() => {
    const memberships = editingUserDetails?.orgMemberships
    if (!memberships || memberships.length === 0) return null
    return memberships[0].organizations?.id ?? null
  }, [editingUserDetails])

  const { data: editingUserOrgExchanges } = useQuery({
    queryKey: ['admin', 'org', editingUserOrgId, 'exchanges'],
    queryFn: () => adminApi.getOrgExchanges(editingUserOrgId!),
    enabled: !!editingUserOrgId,
  })

  const { data: editingUserOrgParticipants } = useQuery({
    queryKey: ['admin', 'org', editingUserOrgId, 'participants'],
    queryFn: () => adminApi.getOrgExchangeParticipants(editingUserOrgId!),
    enabled: !!editingUserOrgId,
  })

  const {
    register: registerImpersonate,
    handleSubmit: handleImpersonateSubmit,
    reset: resetImpersonateForm,
    formState: { errors: impersonateErrors },
  } = useForm<ImpersonateFormValues>({
    resolver: zodResolver(impersonateSchema),
  })

  const {
    register: registerSetPassword,
    handleSubmit: handleSetPasswordSubmit,
    reset: resetSetPasswordForm,
    watch: watchSetPassword,
    formState: { errors: setPasswordErrors, isValid: setPasswordValid },
  } = useForm<SetPasswordFormValues>({
    resolver: zodResolver(setPasswordSchema),
    mode: 'onChange',
  })

  const watchedNewPassword = watchSetPassword('newPassword') ?? ''

  const {
    register: registerCreateUser,
    handleSubmit: handleCreateUserSubmit,
    reset: resetCreateUserForm,
    control: createUserControl,
    watch: watchCreateUser,
    setError: setCreateUserError,
    setValue: setCreateUserValue,
    formState: { errors: createUserErrors, isValid: createUserValid },
  } = useForm<CreateUserFormValues>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      platformRole: null,
      orgId: null,
      orgRole: null,
      sendWelcomeEmail: true,
      exchangeAccess: undefined,
    },
    mode: 'onChange',
  })

  const watchedOrgId = watchCreateUser('orgId')
  const watchedOrgRole = watchCreateUser('orgRole')
  const watchedExchangeAccess = watchCreateUser('exchangeAccess')

  useEffect(() => {
    if (!currentUser || !isPlatformAdmin) {
      navigate('/app')
      return
    }
    loadUsers()
  }, [currentUser, isPlatformAdmin, navigate, page, searchQuery])

  useEffect(() => {
    if (!addUserOpen) {
      setExchangeAccessExpanded(false)
      return
    }
    setOrgsLoading(true)
    setOrgsError(false)
    adminApi.getOrgs()
      .then((result) => setOrgs(result))
      .catch(() => setOrgsError(true))
      .finally(() => setOrgsLoading(false))
    setExchangesLoading(true)
    adminApi.getExchanges({ limit: 100 })
      .then((result) => setExchanges(result.exchanges))
      .catch(() => setExchanges([]))
      .finally(() => setExchangesLoading(false))
  }, [addUserOpen])

  useEffect(() => {
    if (!editingUser || !editingUserOrgParticipants || !editingUserOrgExchanges) return
    const userParticipants = editingUserOrgParticipants.filter(
      (p) => p.userId === editingUser.id && p.active
    )
    const participantByExchange: Record<string, ExchangeRole> = {}
    for (const p of userParticipants) {
      participantByExchange[p.exchangeId] = p.exchangeRole as ExchangeRole
    }
    const initialMap: Record<string, ExchangeRole | null> = {}
    for (const exchange of editingUserOrgExchanges) {
      initialMap[exchange.id] = participantByExchange[exchange.id] ?? null
    }
    const roleCounts: Record<string, number> = {}
    for (const p of userParticipants) {
      roleCounts[p.exchangeRole] = (roleCounts[p.exchangeRole] ?? 0) + 1
    }
    const dominantRole = (Object.entries(roleCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'exchanger') as ExchangeRole
    setPermissionsSelectedRole(dominantRole)
    setPermissionsRoles(initialMap)
    setPermissionsInitialRoles(initialMap)
  }, [editingUser, editingUserOrgParticipants, editingUserOrgExchanges])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getUsers({
        search: searchQuery || undefined,
        page,
        limit: 20,
      })
      setUsers(response.users)
      setTotalPages(response.totalPages)
    } catch {
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (userId: string) => {
    try {
      await adminApi.deleteUser(userId)
      toast.success('User deleted successfully')
      setDeleteTarget(null)
      loadUsers()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete user')
    }
  }

  const handleViewUser = async (user: AdminUser) => {
    try {
      setSelectedUser(user)
      const userDetails = await adminApi.getUser(user.id)
      setSelectedUserDetails(userDetails)
    } catch {
      toast.error('Failed to load user details')
    }
  }

  const handleEditUser = (user: AdminUser) => {
    setEditingUser(user)
    setEditActiveTab('profile')
    setPermissionsSelectedRole('exchanger')
    setPermissionsRoles({})
    setPermissionsInitialRoles({})
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      isIdentityVerified: user.isIdentityVerified,
      isKycComplete: user.isKycComplete,
      isAccreditedInvestor: user.isAccreditedInvestor,
    })
  }

  const handleSaveUser = async () => {
    if (!editingUser) return
    try {
      await adminApi.updateUser(editingUser.id, editForm)
      toast.success('User updated successfully')
      setEditingUser(null)
      loadUsers()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update user')
    }
  }

  const handleSavePermissions = async () => {
    if (!editingUser) return
    setSavingPermissions(true)
    try {
      const exchangeAccess = Object.entries(permissionsRoles)
        .filter(([, role]) => role !== null)
        .map(([exchangeId, role]) => ({ exchangeId, exchangeRole: role as ExchangeRole }))
      await adminApi.updateUser(editingUser.id, { exchangeAccess })
      toast.success('Permissions updated')
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'user', editingUser.id] })
      setEditingUser(null)
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to update permissions')
    } finally {
      setSavingPermissions(false)
    }
  }

  const permissionsChanged = useMemo(() => {
    const currentKeys = Object.keys(permissionsRoles)
    const initialKeys = Object.keys(permissionsInitialRoles)
    if (currentKeys.length !== initialKeys.length) return true
    for (const key of currentKeys) {
      if (permissionsRoles[key] !== permissionsInitialRoles[key]) return true
    }
    return false
  }, [permissionsRoles, permissionsInitialRoles])

  const handleResetNewUser = async (user: AdminUser) => {
    if (user.email !== 'newuser@open1031.ai') {
      toast.error('This reset function is only for the newuser@open1031.ai demo account')
      return
    }
    const ok = await confirm({
      title: 'Reset user demo state?',
      description: 'This will clear their KYC verification, exchange agreement, and wizard data.',
      destructive: true,
      icon: RefreshCw,
      iconColor: 'amber',
    })
    if (!ok) return
    try {
      const result = await adminApi.resetDemoUser(user.id)
      toast.success(result.message || 'Newuser demo state reset successfully')
      loadUsers()
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (
          key.includes('exchange_wizard') ||
          key.includes('exchange_agreement') ||
          key.includes('wizard_state') ||
          key.includes('exchange_docs')
        )) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key))
      toast.success('Local storage cleared. User should refresh the page.')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reset newuser demo state')
    }
  }

  const onImpersonateSubmit = async (values: ImpersonateFormValues) => {
    if (!impersonateTarget) return
    setImpersonating(true)
    try {
      const result = await adminApi.impersonate(impersonateTarget.id, values.reason)
      setAccessToken(result.jwt)
      setImpersonateTarget(null)
      resetImpersonateForm()
      window.location.reload()
    } catch {
      toast.error('Failed to start impersonation')
      setImpersonating(false)
    }
  }

  const onCreateUserSubmit = async (values: CreateUserFormValues) => {
    setCreatingUser(true)
    setSubmitError(null)
    if (values.exchangeAccess?.exchangeId) {
      const selectedExchange = exchanges.find((e) => e.id === values.exchangeAccess?.exchangeId)
      if (selectedExchange && values.orgId && selectedExchange.account?.id !== values.orgId) {
        setSubmitError('User must be a member of the org that owns this exchange')
        setCreatingUser(false)
        return
      }
    }
    try {
      const body: CreateUserBody = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        platformRole: toNullable<PlatformRole>(values.platformRole as string | null),
        orgId: toNullable<string>(values.orgId as string | null),
        orgRole: toNullable<OrgRole>(values.orgRole as string | null),
        sendWelcomeEmail: values.sendWelcomeEmail ?? true,
        exchangeAccess: values.exchangeAccess,
      }
      const response = await adminApi.createUser(body)
      setAddUserOpen(false)
      resetCreateUserForm()
      loadUsers()
      if (response.oneTimeCredentials?.password) {
        setOneTimePassword({ email: values.email, password: response.oneTimeCredentials.password })
      } else {
        toast.success(`Invitation email sent to ${values.email}`)
      }
    } catch (error: any) {
      const status = error?.status ?? error?.response?.status
      if (status === 409) {
        setCreateUserError('email', { message: 'An account with this email already exists' })
      } else {
        setSubmitError(error?.message || 'Failed to create user')
        toast.error(error?.message || 'Failed to create user')
      }
    } finally {
      setCreatingUser(false)
    }
  }

  const handleSendPasswordReset = async () => {
    if (!passwordResetTarget) return
    setSendingReset(true)
    try {
      await adminApi.sendPasswordReset(passwordResetTarget.id)
      toast.success(`Password reset email sent to ${passwordResetTarget.email}`)
      setPasswordResetTarget(null)
    } catch {
      toast.error('Failed to send password reset email')
    } finally {
      setSendingReset(false)
    }
  }

  const handleCopyOneTimePassword = () => {
    if (!oneTimePassword) return
    navigator.clipboard.writeText(oneTimePassword.password)
    setOneTimeCopied(true)
    setTimeout(() => setOneTimeCopied(false), 2000)
  }

  const onSetPasswordSubmit = async (values: SetPasswordFormValues) => {
    if (!setPasswordTarget) return
    setSettingPassword(true)
    try {
      await adminApi.setUserPassword(setPasswordTarget.id, values.newPassword)
      setSetPasswordResult(values.newPassword)
      setSetPasswordResultCopied(false)
      resetSetPasswordForm()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to set password')
    } finally {
      setSettingPassword(false)
    }
  }

  const handleCopySetPassword = () => {
    if (!setPasswordResult) return
    navigator.clipboard.writeText(setPasswordResult)
    setSetPasswordResultCopied(true)
  }

  const selectedOrg = orgs.find((o) => o.id === watchedOrgId)

  const orgOptions = [
    { value: '__none__', label: 'None' },
    ...orgs.map((org) => ({ value: org.id, label: org.name })),
  ]

  const roleFilterOptions: Array<{ value: string; label: string }> = [
    { value: 'platform_admin', label: 'Platform Admin' },
  ]

  const displayedUsers = roleFilter
    ? users.filter((u) => {
        if (roleFilter === 'platform_admin') return u.isAdmin
        return false
      })
    : users

  const initialLoad = loading && users.length === 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
            <Users className="h-7 w-7 text-primary" />
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage all users across the platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={loadUsers}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setAddUserOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              aria-label="Search users"
              placeholder="Search users by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(1)
              }}
              className="pl-9"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {roleFilterOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={roleFilter === option.value}
                onClick={() => setRoleFilter(roleFilter === option.value ? null : option.value)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                  roleFilter === option.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-border/80',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        {initialLoad && (
          <>
            <UserCardSkeleton />
            <UserCardSkeleton />
            <UserCardSkeleton />
            <UserCardSkeleton />
          </>
        )}

        {!initialLoad && displayedUsers.length === 0 && (
          <Card className="py-16">
            <div className="flex flex-col items-center gap-3 text-center">
              <Users className="h-12 w-12 text-muted-foreground/40" />
              <p className="text-base font-medium text-foreground">
                No users yet
              </p>
              <p className="text-sm text-muted-foreground">
                {searchQuery || roleFilter
                  ? 'No users match your current filters.'
                  : 'Get started by adding the first user.'}
              </p>
              {!searchQuery && !roleFilter && (
                <Button
                  size="sm"
                  onClick={() => setAddUserOpen(true)}
                  className="mt-1"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add your first user
                </Button>
              )}
            </div>
          </Card>
        )}

        {!initialLoad && displayedUsers.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            onView={handleViewUser}
            onEdit={handleEditUser}
            onPasswordReset={(u) => setPasswordResetTarget(u)}
            onImpersonate={(u) => setImpersonateTarget(u)}
            onDemoReset={handleResetNewUser}
            onDelete={(u) => setDeleteTarget(u)}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground">User Details</h2>
                <Button
                  variant="outline"
                  onClick={() => setSelectedUser(null)}
                >
                  Close
                </Button>
              </div>
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Name</p>
                    <p className="text-sm text-foreground">{selectedUser.firstName} {selectedUser.lastName}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Email</p>
                    <p className="text-sm text-foreground">{selectedUser.email}</p>
                  </div>
                  {selectedUser.phone && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Phone</p>
                      <p className="text-sm text-foreground">{selectedUser.phone}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Created</p>
                    <p className="text-sm text-foreground">{new Date(selectedUser.createdAt).toLocaleString()}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Status</p>
                  <div className="flex gap-2 flex-wrap">
                    {selectedUser.isIdentityVerified ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200">
                        <XCircle className="h-3 w-3 mr-1" />
                        Pending Verification
                      </span>
                    )}
                    {selectedUser.isKycComplete && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                        <Shield className="h-3 w-3 mr-1" />
                        KYC Complete
                      </span>
                    )}
                    {selectedUser.isAccreditedInvestor && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-300">
                        Accredited Investor
                      </span>
                    )}
                    {selectedUser.isAdmin && (
                      <RoleBadge role="platform_admin" />
                    )}
                    {selectedUser.mfaEnabled && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">
                        <LockKeyhole className="h-3 w-3 mr-1" />
                        MFA Enabled
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Exchanges ({selectedUser.exchangeCount || 0})
                  </p>
                  {selectedUser.exchangeCount === 0 ? (
                    <p className="text-sm text-muted-foreground">No exchanges</p>
                  ) : (
                    <>
                      {selectedUserDetails?.exchanges && selectedUserDetails.exchanges.length > 0 && (
                        <div className="space-y-2 mb-2">
                          {selectedUserDetails.exchanges.map((ex: any) => (
                            <div
                              key={ex.id}
                              className="p-2 bg-muted rounded-lg"
                            >
                              <p className="text-sm font-medium text-foreground">{ex.name}</p>
                              <p className="text-xs text-muted-foreground">Status: {ex.status}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(null)
                          navigate('/app/admin/exchanges')
                        }}
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        View All Exchanges
                      </Button>
                    </>
                  )}
                </div>

                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Organizations ({selectedUser.accountCount || 0})
                  </p>
                  {selectedUserDetails?.accounts && selectedUserDetails.accounts.length > 0 ? (
                    <div className="space-y-2">
                      {selectedUserDetails.accounts.map((acc: any) => (
                        <div
                          key={acc.id}
                          className="p-2 bg-muted rounded-lg"
                        >
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {acc.accountId}
                            </span>
                            {' '}— {roleLabel(acc.role)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Member of {selectedUser.accountCount || 0} organization(s)
                    </p>
                  )}
                </div>

                <div className="flex gap-2 pt-4 border-t border-border flex-wrap">
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleEditUser(selectedUser)
                      setSelectedUser(null)
                    }}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Edit User
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setPasswordResetTarget(selectedUser)
                      setSelectedUser(null)
                    }}
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Reset Password
                  </Button>
                  {!selectedUser.isAdmin && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setImpersonateTarget(selectedUser)
                        setSelectedUser(null)
                      }}
                    >
                      <UserCheck className="h-4 w-4 mr-2" />
                      View as User
                    </Button>
                  )}
                  {selectedUser.email === 'newuser@open1031.ai' && (
                    <Button
                      variant="outline"
                      onClick={() => handleResetNewUser(selectedUser)}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset Demo
                    </Button>
                  )}
                  {!selectedUser.isAdmin && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setDeleteTarget(selectedUser)
                        setSelectedUser(null)
                      }}
                      className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete User
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      <Dialog
        open={!!editingUser}
        onOpenChange={(open) => {
          if (!open) setEditingUser(null)
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogHeaderWithIcon
              icon={Settings}
              iconColor="teal"
              title={`Manage User: ${editingUser?.firstName ?? ''} ${editingUser?.lastName ?? ''}`}
              description={editingUser?.email ?? ''}
            />
          </DialogHeader>

          <div className="border-b border-border -mx-6 px-6">
            <div className="flex gap-1">
              {(['profile', 'permissions', 'admin'] as const).map((tab) => {
                const tabConfig = {
                  profile: { label: 'Profile', Icon: User },
                  permissions: { label: 'Permissions', Icon: Shield },
                  admin: { label: 'Admin', Icon: Crown },
                }[tab]
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setEditActiveTab(tab)}
                    className={cn(
                      'px-4 py-3 font-medium text-sm transition-colors relative',
                      editActiveTab === tab
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <tabConfig.Icon className="h-4 w-4" />
                      {tabConfig.label}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {editActiveTab === 'profile' && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-first-name">First Name</Label>
                  <Input
                    id="edit-first-name"
                    value={editForm.firstName || ''}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    placeholder="First name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-last-name">Last Name</Label>
                  <Input
                    id="edit-last-name"
                    value={editForm.lastName || ''}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="Email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editForm.phone || ''}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="Phone"
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setEditingUser(null)}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveUser}>
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}

          {editActiveTab === 'permissions' && (
            <div className="space-y-5 pt-2">
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Default Role</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Default role applied when adding a user to a new exchange.
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {PARTICIPANT_ROLES.map(({ value, label, description, Icon }) => {
                    const selected = permissionsSelectedRole === value
                    return (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setPermissionsSelectedRole(value)}
                        className={cn(
                          'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                          selected
                            ? 'border-primary bg-primary/5 text-foreground'
                            : 'border-border bg-background text-muted-foreground hover:border-primary/40',
                        )}
                      >
                        <Icon className={cn('mt-0.5 h-4 w-4 flex-shrink-0', selected ? 'text-primary' : '')} />
                        <div>
                          <p className={cn('text-sm font-medium leading-none', selected ? 'text-foreground' : '')}>{label}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-foreground mb-3">Exchange Access</p>
                {!editingUserOrgId && (
                  <p className="text-sm text-muted-foreground">
                    This user has no org membership — no exchanges to assign.
                  </p>
                )}
                {editingUserOrgId && !editingUserOrgExchanges && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    Loading exchanges…
                  </div>
                )}
                {editingUserOrgId && editingUserOrgExchanges && editingUserOrgExchanges.length === 0 && (
                  <p className="text-sm text-muted-foreground">No exchanges in this organisation.</p>
                )}
                {editingUserOrgId && editingUserOrgExchanges && editingUserOrgExchanges.length > 0 && (
                  <div className="rounded-lg border border-border overflow-hidden">
                    {editingUserOrgExchanges.map((exchange, index) => {
                      const rowRole = permissionsRoles[exchange.id] ?? null
                      const checked = rowRole !== null
                      return (
                        <div
                          key={exchange.id}
                          className={cn(
                            'flex items-center gap-3 px-3 py-2.5 transition-colors',
                            index > 0 && 'border-t border-border',
                            checked ? 'bg-background' : 'bg-muted/20',
                          )}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-input text-primary flex-shrink-0"
                            checked={checked}
                            onChange={(e) => {
                              setPermissionsRoles((prev) => ({
                                ...prev,
                                [exchange.id]: e.target.checked ? permissionsSelectedRole : null,
                              }))
                            }}
                            aria-label={exchange.name}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{exchange.name}</p>
                          </div>
                          {checked && (
                            <SelectPrimitive.Root
                              value={rowRole}
                              onValueChange={(newRole) => {
                                setPermissionsRoles((prev) => ({
                                  ...prev,
                                  [exchange.id]: newRole as ExchangeRole,
                                }))
                              }}
                            >
                              <SelectPrimitive.Trigger
                                aria-label={`Role for ${exchange.name}`}
                                className={cn(
                                  'flex h-8 items-center justify-between rounded-md border border-input bg-background px-2 py-1 text-xs',
                                  'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                                  'w-36 flex-shrink-0',
                                )}
                              >
                                <SelectPrimitive.Value />
                                <SelectPrimitive.Icon asChild>
                                  <ChevronDown className="h-3 w-3 opacity-50 ml-1" />
                                </SelectPrimitive.Icon>
                              </SelectPrimitive.Trigger>
                              <SelectPrimitive.Portal>
                                <SelectPrimitive.Content
                                  className={cn(
                                    'relative z-50 max-h-60 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md',
                                    'data-[state=open]:animate-in data-[state=closed]:animate-out',
                                    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                                    'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                                  )}
                                  position="popper"
                                >
                                  <SelectPrimitive.Viewport className="p-1">
                                    {PARTICIPANT_ROLES.map((roleOption) => (
                                      <SelectPrimitive.Item
                                        key={roleOption.value}
                                        value={roleOption.value}
                                        className={cn(
                                          'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-xs outline-none',
                                          'focus:bg-accent focus:text-accent-foreground',
                                        )}
                                      >
                                        <SelectPrimitive.ItemText>{roleOption.label}</SelectPrimitive.ItemText>
                                      </SelectPrimitive.Item>
                                    ))}
                                  </SelectPrimitive.Viewport>
                                </SelectPrimitive.Content>
                              </SelectPrimitive.Portal>
                            </SelectPrimitive.Root>
                          )}
                          {!checked && (
                            <span className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
                              exchange.status === 'active'
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                : 'bg-muted text-muted-foreground',
                            )}>
                              {exchange.status.charAt(0).toUpperCase() + exchange.status.slice(1)}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setEditingUser(null)}
                >
                  Close
                </Button>
                <Button
                  onClick={handleSavePermissions}
                  disabled={!permissionsChanged || savingPermissions}
                >
                  {savingPermissions ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}

          {editActiveTab === 'admin' && (
            <div className="space-y-4 pt-2">
              {editingUser && !editingUser.isAdmin && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Verification Status
                  </p>
                  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.isIdentityVerified || false}
                      onChange={(e) => setEditForm({ ...editForm, isIdentityVerified: e.target.checked })}
                      className="rounded border-input"
                    />
                    Identity Verified
                  </label>
                  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.isKycComplete || false}
                      onChange={(e) => setEditForm({ ...editForm, isKycComplete: e.target.checked })}
                      className="rounded border-input"
                    />
                    KYC Complete
                  </label>
                  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.isAccreditedInvestor || false}
                      onChange={(e) => setEditForm({ ...editForm, isAccreditedInvestor: e.target.checked })}
                      className="rounded border-input"
                    />
                    Accredited Investor
                  </label>
                </div>
              )}
              {editingUser && editingUser.mfaEnabled && (
                <div className="flex items-center gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 px-3 py-2">
                  <LockKeyhole className="h-4 w-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <span className="text-sm text-emerald-800 dark:text-emerald-300 font-medium">MFA enrolled</span>
                </div>
              )}
              {editingUser && !editingUser.isAdmin && (
                <div className="pt-2 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Impersonate
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-destructive/40 text-destructive hover:bg-destructive/10"
                    onClick={() => {
                      setEditingUser(null)
                      setImpersonateTarget(editingUser)
                    }}
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    View as User
                  </Button>
                </div>
              )}
              <div className="pt-2 border-t border-border">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Set Password
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingUser(null)
                    setSetPasswordTarget(editingUser)
                    setSetPasswordResult(null)
                    setSetPasswordResultCopied(false)
                  }}
                >
                  <Key className="h-4 w-4 mr-2" />
                  Set password
                </Button>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setEditingUser(null)}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveUser}>
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!impersonateTarget}
        onOpenChange={(open) => {
          if (!open) {
            setImpersonateTarget(null)
            resetImpersonateForm()
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogHeaderWithIcon
              icon={UserCheck}
              iconColor="teal"
              title="View as user"
              description={
                impersonateTarget
                  ? `Impersonating ${impersonateTarget.firstName} ${impersonateTarget.lastName} (${impersonateTarget.email}). Provide a reason for audit purposes.`
                  : 'Provide a reason for audit purposes.'
              }
            />
          </DialogHeader>
          <form
            onSubmit={handleImpersonateSubmit(onImpersonateSubmit)}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="impersonate-reason">Reason</Label>
              <Textarea
                id="impersonate-reason"
                placeholder="e.g. Customer reported issue with exchange wizard, investigating..."
                aria-invalid={!!impersonateErrors.reason}
                aria-describedby={impersonateErrors.reason ? 'reason-error' : undefined}
                {...registerImpersonate('reason')}
              />
              {impersonateErrors.reason && (
                <p
                  id="reason-error"
                  role="alert"
                  className="text-xs text-destructive"
                >
                  {impersonateErrors.reason.message}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setImpersonateTarget(null)
                  resetImpersonateForm()
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={impersonating}
              >
                <UserCheck
                  className="h-4 w-4 mr-2"
                  aria-hidden="true"
                />
                {impersonating ? 'Starting...' : 'Impersonate'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addUserOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAddUserOpen(false)
            resetCreateUserForm()
            setSubmitError(null)
            setExchangeAccessExpanded(false)
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogHeaderWithIcon
              icon={UserPlus}
              iconColor="teal"
              title="Add User"
              description="Create a new user account. If a welcome email is sent, the user will receive a link to set their password."
            />
          </DialogHeader>

          <form
            onSubmit={handleCreateUserSubmit(onCreateUserSubmit)}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="create-email">Email</Label>
              <Input
                id="create-email"
                type="email"
                placeholder="user@example.com"
                aria-invalid={!!createUserErrors.email}
                aria-describedby={createUserErrors.email ? 'create-email-error' : undefined}
                {...registerCreateUser('email')}
              />
              {createUserErrors.email && (
                <p
                  id="create-email-error"
                  role="alert"
                  className="text-xs text-destructive"
                >
                  {createUserErrors.email.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="create-first-name">First Name</Label>
                <Input
                  id="create-first-name"
                  placeholder="First name"
                  aria-invalid={!!createUserErrors.firstName}
                  aria-describedby={createUserErrors.firstName ? 'create-first-name-error' : undefined}
                  {...registerCreateUser('firstName')}
                />
                {createUserErrors.firstName && (
                  <p
                    id="create-first-name-error"
                    role="alert"
                    className="text-xs text-destructive"
                  >
                    {createUserErrors.firstName.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="create-last-name">Last Name</Label>
                <Input
                  id="create-last-name"
                  placeholder="Last name"
                  aria-invalid={!!createUserErrors.lastName}
                  aria-describedby={createUserErrors.lastName ? 'create-last-name-error' : undefined}
                  {...registerCreateUser('lastName')}
                />
                {createUserErrors.lastName && (
                  <p
                    id="create-last-name-error"
                    role="alert"
                    className="text-xs text-destructive"
                  >
                    {createUserErrors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-platform-role">Platform Role</Label>
              <Controller
                name="platformRole"
                control={createUserControl}
                render={({ field }) => (
                  <SelectField
                    value={field.value ?? '__none__'}
                    onValueChange={(value) => field.onChange(value === '__none__' ? null : value)}
                    placeholder="Select platform role"
                    options={PLATFORM_ROLE_OPTIONS}
                    aria-label="Platform Role"
                  />
                )}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-org">Organization</Label>
              {orgsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <LoadingSpinner size="sm" />
                  Loading orgs...
                </div>
              ) : orgsError ? (
                <p
                  role="alert"
                  className="text-xs text-destructive flex items-center gap-1"
                >
                  <AlertCircle className="h-3.5 w-3.5" />
                  Failed to load organizations. Reload the dialog to try again.
                </p>
              ) : (
                <Controller
                  name="orgId"
                  control={createUserControl}
                  render={({ field }) => (
                    <SelectField
                      value={field.value ?? '__none__'}
                      onValueChange={(value) => {
                        field.onChange(value === '__none__' ? null : value)
                        if (value === '__none__') {
                          setCreateUserValue('orgRole', null, { shouldValidate: true })
                        }
                      }}
                      placeholder="Select organization"
                      options={orgOptions}
                      aria-label="Organization"
                      aria-invalid={!!createUserErrors.orgId}
                      aria-describedby={createUserErrors.orgId ? 'create-org-error' : undefined}
                    />
                  )}
                />
              )}
              {createUserErrors.orgId && (
                <p
                  id="create-org-error"
                  role="alert"
                  className="text-xs text-destructive"
                >
                  {createUserErrors.orgId.message}
                </p>
              )}
            </div>

            {watchedOrgId && (
              <div className="space-y-2">
                <Label>Org Role</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <Controller
                    name="orgRole"
                    control={createUserControl}
                    render={({ field }) => (
                      <>
                        {ORG_ROLE_PRESETS.map((preset) => {
                          const selected = field.value === preset.value
                          const { Icon } = preset
                          return (
                            <button
                              key={preset.value}
                              type="button"
                              onClick={() => field.onChange(selected ? null : preset.value)}
                              aria-pressed={selected}
                              className={cn(
                                'p-3 rounded-xl border-2 text-left transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                                selected
                                  ? preset.selectedBg
                                  : 'border-border bg-card hover:border-border/80',
                              )}
                            >
                              <div className="flex items-center gap-2 mb-1.5">
                                <Icon
                                  className={cn('h-4 w-4', selected ? preset.color : 'text-muted-foreground')}
                                />
                                <span
                                  className={cn(
                                    'text-sm font-semibold',
                                    selected ? preset.selectedText : 'text-foreground',
                                  )}
                                >
                                  {preset.label}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-snug">
                                {preset.description}
                              </p>
                            </button>
                          )
                        })}
                      </>
                    )}
                  />
                </div>
                {createUserErrors.orgRole && (
                  <p
                    id="create-org-role-error"
                    role="alert"
                    className="text-xs text-destructive"
                  >
                    {createUserErrors.orgRole.message}
                  </p>
                )}
                {selectedOrg && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      This user will join{' '}
                      <span className="font-medium text-foreground">
                        {selectedOrg.name}
                      </span>
                      {watchedOrgRole && (
                        <span>
                          {' '}as{' '}
                          <span className="font-medium text-foreground">
                            {roleLabel(watchedOrgRole)}
                          </span>
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between rounded-lg border border-input p-3">
              <div>
                <p className="text-sm font-medium text-foreground">Send welcome email</p>
                <p className="text-xs text-muted-foreground">User receives an email with a link to set their password</p>
              </div>
              <Controller
                name="sendWelcomeEmail"
                control={createUserControl}
                render={({ field }) => (
                  <SwitchPrimitive.Root
                    checked={field.value ?? true}
                    onCheckedChange={field.onChange}
                    aria-label="Send welcome email"
                    className={cn(
                      'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
                      'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      'data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
                    )}
                  >
                    <SwitchPrimitive.Thumb
                      className={cn(
                        'pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0',
                        'transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
                      )}
                    />
                  </SwitchPrimitive.Root>
                )}
              />
            </div>

            <div className="rounded-lg border border-border overflow-hidden">
              <button
                type="button"
                aria-expanded={exchangeAccessExpanded}
                aria-controls="exchange-access-panel"
                onClick={() => setExchangeAccessExpanded((prev) => !prev)}
                className={cn(
                  'w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium transition-colors',
                  'text-foreground hover:bg-muted/50',
                )}
              >
                <span>Exchange access (optional)</span>
                <ChevronRight
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform duration-200',
                    exchangeAccessExpanded && 'rotate-90',
                  )}
                  aria-hidden="true"
                />
              </button>
              {exchangeAccessExpanded && (
                <div
                  id="exchange-access-panel"
                  className="px-3 pb-3 pt-1 border-t border-border space-y-3"
                >
                  <div className="space-y-1.5">
                    <Label htmlFor="exchange-access-exchange">Exchange</Label>
                    {exchangesLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <LoadingSpinner size="sm" />
                        Loading exchanges...
                      </div>
                    ) : (
                      <Controller
                        name="exchangeAccess.exchangeId"
                        control={createUserControl}
                        render={({ field }) => (
                          <SelectField
                            value={field.value ?? '__none__'}
                            onValueChange={(value) => field.onChange(value === '__none__' ? '' : value)}
                            placeholder="Select exchange"
                            options={[
                              { value: '__none__', label: 'None' },
                              ...exchanges.map((ex) => ({ value: ex.id, label: ex.name })),
                            ]}
                            aria-label="Exchange"
                          />
                        )}
                      />
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Exchange role</Label>
                    <Controller
                      name="exchangeAccess.userType"
                      control={createUserControl}
                      render={({ field }) => (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 lg:grid-cols-4">
                          {EXCHANGE_USER_TYPES.map((userType) => {
                            const Icon = EXCHANGE_USER_TYPE_ICONS[userType]
                            const selected = field.value === userType
                            return (
                              <button
                                key={userType}
                                type="button"
                                aria-pressed={selected}
                                onClick={() => field.onChange(selected ? undefined : userType)}
                                className={cn(
                                  'p-2.5 rounded-lg border-2 text-left transition-all text-xs',
                                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                                  selected
                                    ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                    : 'border-border bg-card hover:border-border/80',
                                )}
                              >
                                <div className="flex items-center gap-1.5 mb-1">
                                  <Icon className={cn('h-3.5 w-3.5', selected ? 'text-primary' : 'text-muted-foreground')} />
                                  <span className={cn('font-semibold', selected ? 'text-primary' : 'text-foreground')}>
                                    {EXCHANGE_USER_TYPE_LABELS[userType]}
                                  </span>
                                </div>
                                <p className="text-muted-foreground leading-tight">
                                  {EXCHANGE_USER_TYPE_DESCRIPTIONS[userType]}
                                </p>
                              </button>
                            )
                          })}
                        </div>
                      )}
                    />
                  </div>
                  {watchedExchangeAccess?.userType === 'custom' && (
                    <div className="space-y-1.5">
                      <Label>Feature permissions</Label>
                      <Controller
                        name="exchangeAccess.featurePermissions"
                        control={createUserControl}
                        render={({ field }) => (
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(EXCHANGE_FEATURE_LABELS).map(([featureKey, featureLabel]) => {
                              const Icon = EXCHANGE_FEATURE_ICONS[featureKey]
                              const enabled = field.value?.includes(featureKey) ?? false
                              return (
                                <button
                                  key={featureKey}
                                  type="button"
                                  aria-pressed={enabled}
                                  onClick={() => {
                                    const current = field.value ?? []
                                    field.onChange(
                                      enabled
                                        ? current.filter((f) => f !== featureKey)
                                        : [...current, featureKey],
                                    )
                                  }}
                                  className={cn(
                                    'p-2.5 rounded-lg border-2 text-left transition-all text-xs',
                                    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                                    enabled
                                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                      : 'border-border bg-card hover:border-border/80',
                                  )}
                                >
                                  <div className="flex items-center gap-1.5">
                                    {Icon && <Icon className={cn('h-3.5 w-3.5', enabled ? 'text-primary' : 'text-muted-foreground')} />}
                                    <span className={cn('font-medium', enabled ? 'text-primary' : 'text-foreground')}>
                                      {featureLabel}
                                    </span>
                                  </div>
                                </button>
                              )
                            })}
                          </div>
                        )}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {submitError && (
              <div
                role="alert"
                className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {submitError}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAddUserOpen(false)
                  resetCreateUserForm()
                  setSubmitError(null)
                  setExchangeAccessExpanded(false)
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creatingUser || !createUserValid}
              >
                <UserPlus
                  className="h-4 w-4 mr-2"
                  aria-hidden="true"
                />
                {creatingUser ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!passwordResetTarget}
        onOpenChange={(open) => {
          if (!open) setPasswordResetTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <DialogHeaderWithIcon
              icon={KeyRound}
              iconColor="violet"
              title="Send password reset email?"
              description={
                passwordResetTarget
                  ? `This will send a password reset email to ${passwordResetTarget.email}. The user will receive a link to set a new password.`
                  : 'The user will receive a link to set a new password.'
              }
              variant="alertDialog"
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPasswordResetTarget(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSendPasswordReset}
              disabled={sendingReset}
            >
              {sendingReset ? 'Sending...' : 'Send Reset Email'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <DialogHeaderWithIcon
              icon={Trash2}
              iconColor="rose"
              title="Delete user?"
              description={
                deleteTarget
                  ? `This will permanently delete ${deleteTarget.firstName} ${deleteTarget.lastName} (${deleteTarget.email}). This action cannot be undone.`
                  : 'This action cannot be undone.'
              }
              variant="alertDialog"
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTarget(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleDeleteUser(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!setPasswordTarget}
        onOpenChange={(open) => {
          if (!open) {
            setSetPasswordTarget(null)
            setSetPasswordResult(null)
            setSetPasswordResultCopied(false)
            resetSetPasswordForm()
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogHeaderWithIcon
              icon={Key}
              iconColor="violet"
              title={`Set password for ${setPasswordTarget?.firstName ?? ''} ${setPasswordTarget?.lastName ?? ''}`}
              description="Admin-set passwords bypass email verification. Share the new password securely."
            />
          </DialogHeader>
          {!setPasswordResult ? (
            <form
              onSubmit={handleSetPasswordSubmit(onSetPasswordSubmit)}
              className="space-y-4"
            >
              <div className="space-y-1.5">
                <Label htmlFor="set-new-password">New password</Label>
                <Input
                  id="set-new-password"
                  type="password"
                  placeholder="Enter new password"
                  aria-invalid={!!setPasswordErrors.newPassword}
                  aria-describedby={setPasswordErrors.newPassword ? 'set-new-password-error' : undefined}
                  {...registerSetPassword('newPassword')}
                />
                <PasswordStrengthIndicator password={watchedNewPassword} />
                {setPasswordErrors.newPassword && (
                  <p
                    id="set-new-password-error"
                    role="alert"
                    className="text-xs text-destructive"
                  >
                    {setPasswordErrors.newPassword.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="set-confirm-password">Confirm password</Label>
                <Input
                  id="set-confirm-password"
                  type="password"
                  placeholder="Confirm new password"
                  aria-invalid={!!setPasswordErrors.confirmPassword}
                  aria-describedby={setPasswordErrors.confirmPassword ? 'set-confirm-password-error' : undefined}
                  {...registerSetPassword('confirmPassword')}
                />
                {setPasswordErrors.confirmPassword && (
                  <p
                    id="set-confirm-password-error"
                    role="alert"
                    className="text-xs text-destructive"
                  >
                    {setPasswordErrors.confirmPassword.message}
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSetPasswordTarget(null)
                    resetSetPasswordForm()
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={settingPassword || !setPasswordValid}
                >
                  <Key className="h-4 w-4 mr-2" aria-hidden="true" />
                  {settingPassword ? 'Setting…' : 'Set password'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800 p-4">
                <p className="text-xs font-medium text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-2">
                  New password set
                </p>
                <p className="text-sm text-foreground font-mono break-all">
                  {setPasswordResult}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                  Share this securely with the user. They should change it on first login.
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleCopySetPassword}
              >
                {setPasswordResultCopied ? (
                  <>
                    <Check className="h-4 w-4 mr-2 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy password
                  </>
                )}
              </Button>
              <DialogFooter>
                <Button
                  disabled={!setPasswordResultCopied}
                  onClick={() => {
                    setSetPasswordTarget(null)
                    setSetPasswordResult(null)
                    setSetPasswordResultCopied(false)
                  }}
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!oneTimePassword}
        onOpenChange={(open) => {
          if (!open) {
            setOneTimePassword(null)
            setOneTimeCopied(false)
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogHeaderWithIcon
              icon={KeyRound}
              iconColor="amber"
              title="User Created — Save Credentials"
              description="This is the only time this temporary password will be shown. Share it securely with the user and ask them to change it immediately."
            />
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800 p-4">
              <p className="text-xs font-medium text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-2">
                Temporary Credentials
              </p>
              <div className="space-y-1">
                <p className="text-sm text-foreground">
                  <span className="font-medium">Email:</span> {oneTimePassword?.email}
                </p>
                <p className="text-sm text-foreground font-mono break-all">
                  <span className="font-sans font-medium">Password:</span> {oneTimePassword?.password}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleCopyOneTimePassword}
            >
              {oneTimeCopied ? (
                <>
                  <Check className="h-4 w-4 mr-2 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy email + password
                </>
              )}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => { setOneTimePassword(null); setOneTimeCopied(false) }}>
              Done — I have saved the credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
